import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listRankingModels, cloneRankingModel } from "../../api/clientVendorDirectoryRankingApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import ClientVendorDirectoryRankingSubnav from "../../components/casamap/admin/ClientVendorDirectoryRankingSubnav";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.ranking";
const MODEL_STATUSES = ["draft", "testing", "active", "retired", "rejected"];

export default function ClientVendorDirectoryRankingModelListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [models, setModels] = useState([]);
  const [activeModelId, setActiveModelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const status = searchParams.get("status") || "";
  const base = `${serviciosBase}/posicionamiento/modelos`;
  const viewName = activeView?.name || t(`${COPY_PREFIX}.thisView`);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listRankingModels(clientViewId, { status: status || undefined });
      setModels(body.models || []);
      setActiveModelId(body.activeModelId);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.models.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleClone = async (modelId) => {
    setBusy(true);
    try {
      const body = await cloneRankingModel(clientViewId, modelId, {
        changeSummary: t(`${COPY_PREFIX}.models.cloneSummary`),
      });
      window.location.assign(`${base}/${body.model.id}`);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.models.loadError`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <ClientVendorDirectoryRankingSubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.models.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.models.intro`, { viewName })}</p>
        </div>
      </div>

      <div className="cvd-filters">
        <div className="cvd-field">
          <label htmlFor="model-status">{t(`${COPY_PREFIX}.models.statusLabel`)}</label>
          <select
            id="model-status"
            value={status}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set("status", e.target.value);
              else next.delete("status");
              setSearchParams(next);
            }}
          >
            <option value="">{t(`${COPY_PREFIX}.models.allStatuses`)}</option>
            {MODEL_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`${COPY_PREFIX}.models.status.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.models.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage ? (
        <div className="cvd-table-wrap">
          <table className="cvd-table">
            <thead>
              <tr>
                <th>{t(`${COPY_PREFIX}.models.table.name`)}</th>
                <th>{t(`${COPY_PREFIX}.models.table.version`)}</th>
                <th>{t(`${COPY_PREFIX}.models.table.status`)}</th>
                <th>{t(`${COPY_PREFIX}.models.table.activated`)}</th>
                <th>{t(`${COPY_PREFIX}.models.table.actions`)}</th>
              </tr>
            </thead>
            <tbody>
              {models.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    {t(`${COPY_PREFIX}.models.empty`)}
                  </td>
                </tr>
              ) : (
                models.map((model) => (
                  <tr key={model.id}>
                    <td>
                      <Link to={`${base}/${model.id}`}>{model.name}</Link>
                      {model.id === activeModelId ? (
                        <span className="cvd-badge cvd-badge--published">
                          {t(`${COPY_PREFIX}.models.activeBadge`)}
                        </span>
                      ) : null}
                    </td>
                    <td>v{model.version}</td>
                    <td>
                      {t(`${COPY_PREFIX}.models.status.${model.status}`) || formatStatusLabel(model.status)}
                    </td>
                    <td>{model.activatedAt ? formatClientDate(model.activatedAt, locale) : "—"}</td>
                    <td>
                      <Link className="btn btn-small" to={`${base}/${model.id}`}>
                        {t(`${COPY_PREFIX}.models.open`)}
                      </Link>
                      <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.RANKING_CONFIGURE}>
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={busy}
                          onClick={() => handleClone(model.id)}
                        >
                          {t(`${COPY_PREFIX}.models.clone`)}
                        </button>
                      </VendorDirectoryPermissionGate>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
